import { Link } from "react-router-dom";
import type { Book, BookWriter } from "../api";
import { useT } from "../i18n";
import CharacterFace from "./CharacterFace";
import { ForwardIcon } from "./icons";
import Logo from "./Logo";

function byRole(book: Book, role: BookWriter["role"]): BookWriter[] {
  return (book.writers ?? [])
    .filter((w) => w.role === role)
    .sort((a, b) => a.position - b.position);
}

/**
 * WriterPill is one writer as a pill to their page: the face (or the initial on
 * the sage circle, as the writer page draws it), the name, and a chevron.
 * Filled, with the face on the start side, so it reads as a person and not as
 * one of the outlined tag chips below it — an underlined name "doesn't look
 * nice" (owner, 2026-10-19). The pill is the interface's, so its layout follows
 * the page; the name is authored text, so `dir="auto"` sits on the name alone.
 *
 * At least 44px tall at both sizes: the padding makes the tap target.
 */
/** The platform's own writer, credited on the books SageLeaf translated: seeded with this id (migration 000118). */
const SAGELEAF_WRITER = "01920000-0000-7000-8000-0000000005a1";

function WriterPill({ writer, small }: { writer: BookWriter; small?: boolean }) {
  return (
    <Link
      to={`/writers/${writer.id}`}
      data-testid="writer-link"
      className={`inline-flex min-h-11 max-w-full items-center rounded-full bg-sunk text-ink transition-colors hover:bg-line ${
        small ? "gap-1.5 py-2 ps-1.5 pe-2.5 text-xs" : "gap-2 py-2 ps-2 pe-3 text-sm"
      }`}
    >
      {/* Our own translations carry the mark, not an initial: the book is ours. */}
      {writer.id === SAGELEAF_WRITER ? (
        <span className="grid size-[22px] shrink-0 place-items-center" data-testid="writer-mark">
          <Logo size={18} decorative />
        </span>
      ) : (
        <CharacterFace character={writer} size={small ? 22 : 28} />
      )}
      <span dir="auto" data-testid="writer-link-name" className="min-w-0 truncate font-medium">
        {writer.name}
      </span>
      <ForwardIcon className={`shrink-0 text-muted ${small ? "size-3" : "size-3.5"}`} />
    </Link>
  );
}

/**
 * BookWriters is a book's authors as pills to their pages, and its
 * translators on a quieter line beneath (requirement 0118). An older server
 * sends only the author line, which is shown as it was.
 */
export default function BookWriters({ book, className = "" }: { book: Book; className?: string }) {
  const t = useT();
  const authors = byRole(book, "author");
  const translators = byRole(book, "translator");
  if (authors.length === 0 && translators.length === 0) {
    return book.author ? (
      <p dir="auto" className={`text-sm text-ink-soft ${className}`}>
        {book.author}
      </p>
    ) : null;
  }
  return (
    <div className={className}>
      {authors.length > 0 && (
        <ul data-testid="book-authors" className="flex flex-wrap justify-center gap-2">
          {authors.map((w) => (
            <li key={w.id} className="max-w-full">
              <WriterPill writer={w} />
            </li>
          ))}
        </ul>
      )}
      {translators.length > 0 && (
        <div
          data-testid="book-translators"
          className="mt-1 flex flex-wrap items-center justify-center gap-x-1.5"
        >
          <span className="text-xs text-muted">{t.writer.translatedBy}</span>
          {translators.map((w) => (
            <WriterPill key={w.id} writer={w} small />
          ))}
        </div>
      )}
    </div>
  );
}
